import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";

import { InvestmentStyleDiagnosis } from "@/api/generated";
import { fetchInvestmentStyleDiagnosis, isRenderableDiagnosisBody } from "@/api/investmentStyleDiagnosis";
import { useInvestmentStyleFunnelEvent } from "@/hooks/useInvestmentStyleFunnelEvent";
import { AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { extractHttpStatus } from "@/utilities/extractHttpStatus";
import { clearCompleted } from "@/utilities/investmentStyleDiagnosisStorage";

interface UseGetInvestmentStyleDiagnosis {
  diagnosis: InvestmentStyleDiagnosis | null;
  loading: boolean;
  // 404（未回答）はイントロへ自動遷移するため対象外。500・タイムアウト等、再試行の余地があるエラーのみを表す。
  error: boolean;
  // セッション切れ(401)。再試行では復帰しないため error とは別に扱い、再ログインを案内する。
  sessionExpired: boolean;
  retry: () => void;
}

export const useGetInvestmentStyleDiagnosis = (): UseGetInvestmentStyleDiagnosis => {
  const history = useHistory();
  const location = useLocation();
  const [diagnosis, setDiagnosis] = useState<InvestmentStyleDiagnosis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  // retry() を呼ぶたびにインクリメントし、下の useEffect を再実行させる。
  const [retryCount, setRetryCount] = useState(0);

  // 履歴エントリの state に診断結果が残っていても採用せず、必ずサーバへ問い合わせる。
  // ログアウトが履歴 state を消さないため、state を信用するとアカウント切替後に
  // 前ユーザーの診断結果を表示しうる（サーバ確認なら現セッションの本人のものしか返らない）。
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted) {
        setLoading(true);
        setError(false);
        setSessionExpired(false);
      }
      try {
        const data = await fetchInvestmentStyleDiagnosis();
        if (mounted) {
          // 200 でも空ボディ等が返りうる。`{}` のような形だけのボディは壊れたレスポンスとして
          // 再試行導線に倒す。一方、customer_type は API 側で allow_nil のため必須にはせず、
          // 欠けていても本人のデータが見られるよう描画に倒す（画面側が個別に null ガードしている）
          if (!isRenderableDiagnosisBody(data)) {
            setLoading(false);
            setError(true);
            return;
          }
          setDiagnosis(data);
          setLoading(false);
        }
      } catch (error) {
        // 未回答(404)なら診断フローの入口(イントロ)へ戻す。
        // このアカウントには診断結果が存在しないと判明したタイミングなので、セッション内限定の完了フラグを
        // ここで消しておく。消さないと isCompleted() が真のままフォーム→結果画面→(再度404)→イントロ…の
        // 無限ループに陥る（ログアウトを経由しないセッション切替等で発生しうる）。
        // 保存済みの回答（clearSavedAnswers）はここでは消さない。診断途中の状態でブックマーク・戻る進む等
        // により直接この画面へ到達しただけの可能性があり、消すと入力中の回答を失わせてしまうため。
        const status = extractHttpStatus(error);
        if (status === 404) {
          clearCompleted();
          // 入口の state（fromHome）をイントロへ引き継ぐ。ホーム起点で差し戻された場合に
          // チップスが出たり IntroAccess イベントが誤送信されたりしないようにするため
          history.replace("/investment_style_diagnosis/intro", location.state);
          return;
        }
        if (status === 401) {
          // セッション切れは再試行しても成功しないため、送信側（useSubmitInvestmentStyleDiagnosis）と
          // 同じ「リロードして再ログイン」の案内に倒す。再試行導線(error/retry)とは分けて扱う。
          if (mounted) {
            setLoading(false);
            setSessionExpired(true);
          }
          return;
        }
        // 500・タイムアウト等、404/401以外の失敗。再試行で復帰しうるため画面側に再読み込み導線を出す。
        if (mounted) {
          setLoading(false);
          setError(true);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [history, location.state, retryCount]);

  // 取得完了後に1回だけ送る。タイプ別に以降の遷移を追えるよう customer_type を付与する。
  useInvestmentStyleFunnelEvent(
    diagnosis ? AppEventTokenEnum.InvestmentStyleDiagnosisResultAccess : undefined,
    diagnosis?.customer_type ? { customer_type: diagnosis.customer_type } : undefined
  );

  const retry = () => setRetryCount((count) => count + 1);

  return { diagnosis, loading, error, sessionExpired, retry };
};
